'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

interface NavItem {
  title: string
  slug: string
  order?: number
}

interface NavSection {
  title: string
  icon?: string
  items: NavItem[]
}

export default function Sidebar({ sections }: { sections: NavSection[] }) {
  const pathname = usePathname()
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    Object.fromEntries(sections.map(s => [s.title, true]))
  )

  const toggle = (title: string) => {
    setExpanded(prev => ({ ...prev, [title]: !prev[title] }))
  }

  return (
    <aside className="w-72 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto border-r border-slate-200 bg-slate-50 p-4">
      {/* Title */}
      <Link href="/docs" className="flex items-center gap-2 mb-6 px-2">
        <span className="text-lg font-bold tracking-wider text-slate-900" style={{ fontFamily: 'var(--font-mono)' }}>
          Документация
        </span>
        <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-700 rounded-full font-semibold">
          v2.0
        </span>
      </Link>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Поиск..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/20"
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="space-y-1">
        {sections.map(section => (
          <div key={section.title}>
            <button
              onClick={() => toggle(section.title)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <span>{section.icon}</span>
              <span className="flex-1 text-left">{section.title}</span>
              <svg
                className={`w-3.5 h-3.5 transition-transform ${expanded[section.title] ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {expanded[section.title] && (
              <div className="ml-4 mt-0.5 space-y-0.5">
                {section.items.map(item => {
                  const href = `/docs/${item.slug}`
                  const isActive = pathname === href
                  return (
                    <Link
                      key={item.slug}
                      href={href}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-50 text-purple-700 font-medium'
                          : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-purple-600' : 'bg-slate-300'}`} />
                      {item.title}
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-200 space-y-2">
        <a
          href="https://github.com/devorbit/uaip"
          target="_blank"
          className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-500 hover:text-purple-700 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
          </svg>
          GitHub
        </a>
        <p className="px-2 text-xs text-slate-400">v2.0.0 • UAIP Docs</p>
      </div>
    </aside>
  )
}
